import { type FetchPolicy } from '@apollo/client';
import { useCallback } from 'react';

import { useQuery, useQueryClient } from '@fhs/client';
import {
  type IUserAccountsQuery,
  UserAccountsDocument,
} from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import { client } from '@fhs-legacy/frontend/src/state/graphql/client';
import { useLocale } from '@fhs-legacy/frontend/src/state/intl';

type UserAccountsData = NonNullable<IUserAccountsQuery['userAccounts']>;

const userAccountsQueryKey = (feCountryCode: string) => ['userAccounts', feCountryCode] as const;

async function fetchUserAccounts(feCountryCode: string, fetchPolicy: FetchPolicy) {
  const { data } = await client.query({
    query: UserAccountsDocument,
    variables: { feCountryCode },
    fetchPolicy,
  });
  return data.userAccounts as UserAccountsData;
}

export async function fetchUserAccountsFromNetwork(feCountryCode: string) {
  return fetchUserAccounts(feCountryCode, 'network-only');
}

export { userAccountsQueryKey };

export const useUserAccountsQuery = (options?: { enabled?: boolean }) => {
  const { feCountryCode } = useLocale();
  const queryClient = useQueryClient();
  const enabled = options?.enabled ?? true;

  const query = useQuery({
    queryKey: userAccountsQueryKey(feCountryCode),
    // Always network — same as payment methods page; do not reuse Apollo cache from cart preload.
    queryFn: () => fetchUserAccountsFromNetwork(feCountryCode),
    enabled,
  });

  const refetch = useCallback(async () => {
    const userAccounts = await queryClient.fetchQuery({
      queryKey: userAccountsQueryKey(feCountryCode),
      queryFn: () => fetchUserAccountsFromNetwork(feCountryCode),
    });
    return { data: userAccounts };
  }, [feCountryCode, queryClient]);

  return {
    ...query,
    refetch,
  };
};
